<script lang="tsx">
  import { useId, type PropType } from "vue";
  import { Button, FloatLabel, InputText, Message } from "primevue";
  import { Form, type FormResolverOptions, type FormSubmitEvent } from "@primevue/forms";

  import { FontAwesomeIcon as Icon } from "@fortawesome/vue-fontawesome";
  import { faArrowRight, faCircleNotch } from "@fortawesome/free-solid-svg-icons";


  export default {
    components: {
      Form, Button, FloatLabel, InputText, Icon, Message
    },
    setup() {
      return { id: useId() };
    },
    props: {
      site: {
        type: String as PropType<"enca" | "frca">,
        required: true,
      }
    },
    data() { return {
      faArrowRight, faCircleNotch,
      submitting: false,
      serverError: undefined as string | undefined,
      successMessage: undefined as string | undefined,
    }},
    methods: {
      resolve({ values }: FormResolverOptions): Record<string, any> {
        const errors: Record<string, any[]> = { username: [] };

        if (values.username == null || values.username === "") {
          errors.username.push({ message: "Email address or username is required" });
        }

        return { errors };
      },

      async submit(submit: FormSubmitEvent) {
        if (!submit.valid) { return; }
        try {
          this.submitting = true;
          const result = await this.$session.sendPasswordResetEmail(
            submit.states.username.value
          );
          if (result.errorCode !== undefined) {
            this.serverError = result.message;
          } else if (result.returnUrl !== undefined) {
            this.serverError = undefined;
            location.href = result.returnUrl;
          } else {
            this.successMessage = result.message;
          }
        } finally {
          this.submitting = false;
        }
      },
    },
  };
</script>

<template>
  <Form v-slot="form" class="form" :initial-values="{username: null, password: null}"
    validate-on-value-update validate-on-submit validate-on-blur
    :resolver="resolve" @submit="submit">
    <h3>Reset Password</h3>

    <div v-if="successMessage === undefined" style="display: contents">
      <FloatLabel variant="on">
        <InputText :id="`${id}-username`" name="username" fluid required/>
        <label :for="`${id}-username`">
          Email Address or Username<!--
          --><span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message v-if="(form.username?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.username.error.message }}
      </Message>

      <Message v-if="serverError !== undefined"
        severity="error" size="small" variant="simple">
        {{ serverError }}
      </Message>

      <Button type="submit" label="Reset Password" icon-pos="right"
        formnovalidate :disabled="!form.valid || submitting">
        <template #icon="slot">
          <Icon :class="[slot.class]" :icon="submitting ? faCircleNotch : faArrowRight" :spin="submitting"/>
        </template>
      </Button>
    </div>

    <div v-else style="display: contents">
      <span class="success">{{ successMessage }}</span>
    </div>

    <div class="extra-links">
      <a href="/en-ca/account/login">Back to login</a>
    </div>
  </Form>
</template>

<style lang="less" scoped>
  h3 { text-align: center; }

  .form {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-inline);
    justify-content: center;
    align-content: center;

    max-width: 35em;
    margin: 0 auto;
  }

  .extra-links {
    display: inline-flex;
    flex-direction: row;
    * {
      flex: 1 0;
      text-align: center;
    }
  }

  .required-star::before {
    content: "*";
    color: var(--p-floatlabel-invalid-color);
  }

  .success { text-align: center; }
</style>
